import {
  filingDestinationPhrase,
  type FilingDestinationModel,
} from '@/hooks/finance/usePortalUploadModel';

/**
 * Jurisdiction-aware copy for the "Mark as reported" carve-out on a Done money
 * row (the treasurer already filed this transaction directly, outside our
 * export, so it must NOT be re-exported). Derived from `usePortalUploadModel()`
 * — the one place the filing-model boundary is resolved — so this panel names
 * the campaign's OWN destination instead of hardcoding "the AL FCPA portal".
 *
 * Portal-upload jurisdictions (AL FCPA today, VA COMET, …) name their portal.
 * Federal (`certifyAtGenerate`, #482) stays jurisdiction-neutral ("the filing
 * portal"): its `portalName` is the generic "state portal" fallback (wrong for
 * federal, which files at efo.fec.gov), and the FEC-copy discipline forbids naming
 * "FEC" on shared finance surfaces — so `filingDestinationPhrase` names neither.
 */
export function markReportedElsewhereCopy(
  model: FilingDestinationModel,
): { trigger: string; placeholder: string; helper: string } {
  const destination = filingDestinationPhrase(model);

  return {
    // The quiet disclosure line. States the precondition so a treasurer who did
    // NOT file it directly self-selects out (this is the split from "Exclude").
    trigger: 'Already filed this directly?',
    placeholder: `e.g. entered directly in ${destination}`,
    // Shown only once the treasurer opens the panel, so it explains the action in
    // context rather than competing for attention. Leads with the distinguishing
    // fact (it stays ON the report, unlike Exclude which takes it off).
    helper: "It stays on your report. We just won't file it again, so it isn't submitted twice.",
  };
}
